import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, useAuth } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { BrandLogo } from "@/components/site/Header";

const input = "mt-2 block w-full bg-transparent border-b border-pine/40 py-2 text-base normal-case tracking-normal focus:outline-none focus:border-brass transition-colors";

export const AdminShell = ({ title, children }) => (
  <div className="site min-h-screen bg-sage text-pine flex flex-col">
    <header className="max-w-6xl w-full mx-auto px-6 py-6">
      <Link to="/" className="inline-flex items-center gap-3" data-testid="admin-home-link">
        <BrandLogo className="h-14" />
      </Link>
    </header>
    <main className="flex-1 w-full max-w-md mx-auto px-6 py-16">
      <p className="lb text-brass-ink">Private inbox</p>
      <h1 className="dp mt-4 !text-5xl">{title}</h1>
      {children}
    </main>
  </div>
);

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(email, password);
      else {
        await adminApi.post("/auth/forgot-password", { email });
        setSent(true);
        toast.success("If that email belongs to the inbox, a reset link is on its way.");
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title={mode === "login" ? "Sign in" : "Reset password"}>
      {sent && mode === "forgot" ? (
        <p className="mt-8 rounded-2xl bg-paper p-5" data-testid="forgot-sent">Check your email for a link to choose a new password. It works for one hour.</p>
      ) : (
        <form onSubmit={submit} className="mt-10 grid gap-6" data-testid="admin-login-form">
          <label className="lb">Email<input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="admin-email-input" /></label>
          {mode === "login" && (
            <label className="lb">Password<input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} data-testid="admin-password-input" /></label>
          )}
          {error && <p className="text-sm text-red-800" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} className="mt-2 rounded-full bg-pine text-sage py-4 transition-colors hover:bg-pine-soft disabled:opacity-70" data-testid="admin-login-submit">
            {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Email me a reset link"}
          </button>
        </form>
      )}
      <button type="button" onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setSent(false); setError(""); }}
        className="mt-6 text-sm underline underline-offset-4 decoration-brass" data-testid="admin-toggle-forgot">
        {mode === "login" ? "Forgot your password?" : "Back to sign in"}
      </button>
    </AdminShell>
  );
}
